/**
 * AppLayout component - Unified layout for all app pages
 * Uses AppSidebar which changes menus based on URL
 */

import type { ReactNode } from "react"
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar"
import { AppProvider } from "@/contexts/app-context"
import { useVisualViewport } from "@/hooks/use-visual-viewport"
import { AppSidebar } from "./sidebar/app-sidebar"
import { CoworkBridge } from "@/components/cowork/cowork-bridge"
import { AppMobileTopBar } from "./app-mobile-top-bar"
import { AppSettingsDialog } from "@/components/apps/app-settings-dialog"
import { WorkspaceSettingsDialog } from "@/components/settings/workspace-settings-dialog"
import { ChannelLinkResume } from "./channel-link-resume"

/**
 * AppLayout component props
 */
interface AppLayoutProps {
  children: ReactNode
}

/**
 * AppLayout component - Unified layout for all app pages
 * AppProvider wraps both sidebar and content so both can access app data
 */
export function AppLayout({ children }: AppLayoutProps) {
  useVisualViewport()
  return (
    <AppProvider>
      <SidebarProvider
        style={{ "--sidebar-width": "280px" } as React.CSSProperties}
      >
        {/*
          Focus mode (#1378) is handled inside AppSidebar: when it's on, the
          sidebar collapses to zero width via its built-in width/left
          transitions (offcanvas), so the chat + canvas expand smoothly instead
          of the sidebar snapping away.
        */}
        <AppSidebar />
        {/*
          Height tracks the VISUAL viewport (`--app-vh`, published by
          useVisualViewport) instead of `100dvh`. iOS Safari ignores the
          `interactive-widget=resizes-content` hint and does not shrink dvh
          when the soft keyboard opens, which left the chat composer hidden
          behind the keyboard. Sizing the page shell to the visible area
          lifts the composer (anchored at the bottom of the chat's flex
          column) to sit just above the keyboard. Falls back to 100dvh
          before the hook runs and on browsers without visualViewport.
        */}
        {/*
          `margin-top: --app-vv-offset-top` re-aligns the shell with the visible
          viewport when the mobile keyboard pans the layout viewport down (iOS
          Safari/Chrome). `--app-vv-offset-top` is 0 on desktop and whenever no
          keyboard is open, so this is a no-op there. (Margin, not `transform`:
          a transform — even `translateY(0)` — would make the shell a containing
          block for `position: fixed` descendants and shift modals/panels.)
          Sized to the visible area via `--app-vh` so the bottom-anchored chat
          composer rides just above the keyboard instead of behind it.
        */}
        <SidebarInset
          className="app-vv-shell bg-white dark:bg-background h-[var(--app-vh,100dvh)] overflow-y-auto"
          style={{ marginTop: "var(--app-vv-offset-top, 0px)" }}
        >
          <AppMobileTopBar />
          {children}
        </SidebarInset>
      </SidebarProvider>
      {/* CoWork (desktop only): stable headless subscriber keeping the store in
          sync with the shell (approvals, changes, connection). The approval CARD
          renders above the composer (in MultimodalInput). No-op in the web app. */}
      <CoworkBridge />
      {/* Modal settings surfaces — single mount, opened from anywhere via
          useSettingsDialogsStore. Inside AppProvider so AppSettingsView can
          read the current app via useApp(). */}
      <WorkspaceSettingsDialog />
      <AppSettingsDialog />
      {/* Sends the user back to /link/channel when a channel-link flow had to
          detour through the workspace's SSO (whose callback lands on /app). */}
      <ChannelLinkResume />
    </AppProvider>
  )
}

AppLayout.displayName = "AppLayout"
